import { ModalBareboneProps, styled } from '@zeiss/beyond-online-react';
import {
  AlertText,
  GeneralModal,
  ModalText,
  StyledContainer,
} from '@zeiss/lib-components';
import { useBreakpoint } from '@zeiss/lib-helpers';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { OutletContextType } from './';
import { showToast } from 'src/helpers/showToast';
import { useDeleteApplicationList } from 'src/hooks/api';

const ModalTextContent = styled.div<StyledContainer>`
  & > p:first-of-type {
    margin: 0;
  }
`;

interface DeleteApplicationListModalProps extends Pick<
  ModalBareboneProps,
  'isOpen' | 'onClose'
> {
  data: Pick<OutletContextType, 'applicationListId' | 'applicationListName'>;
}

export const DeleteApplicationListModal = ({
  data: { applicationListId, applicationListName },
  isOpen,
  onClose,
}: DeleteApplicationListModalProps) => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const { isPending, mutate: deleteApplicationList } =
    useDeleteApplicationList(applicationListId);

  const isMinTablet = useBreakpoint('tablet');

  const removeApplicationList = () => {
    deleteApplicationList(undefined, {
      onError: () => {
        showToast({
          props: {
            title: t('common:notification.errorX', {
              action: t('common:action.deleted'),
              x: t('common:glossary.applicationList'),
            }),
            type: 'error',
          },
        });
      },
      onSuccess: () => {
        showToast({
          props: {
            title: t('common:notification.successX', {
              action: t('common:action.deleted'),
              x: t('common:glossary.applicationList'),
            }),
            type: 'success',
          },
        });
        onClose?.();
        navigate('/tenant-management');
      },
    });
  };

  return (
    <GeneralModal
      acceptButtonText={t('common:action.delete')}
      declineButtonText={t('common:action.cancel')}
      headline={t('common:action.deleteX', {
        x: t('common:glossary.applicationList'),
      })}
      isOpen={isOpen}
      loading={isPending}
      onAccept={removeApplicationList}
      onClose={onClose}
      onDecline={onClose}
      size="m"
    >
      <ModalTextContent $isMinTablet={isMinTablet}>
        <ModalText>
          <Trans
            i18nKey="common:text.deleteApplicationListX"
            t={t}
            values={{ applicationListName }}
          >
            <b />
          </Trans>
        </ModalText>

        <AlertText
          icon="StatusActionRequired"
          text={t('common:text.deleteApplicationListWarning')}
        />
      </ModalTextContent>
    </GeneralModal>
  );
};
